import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { api, unwrap } from "@/api/client";
import type { SearchQuery } from "@/api/types";
import { sessionId } from "@/lib/session";
import { cursorFrom } from "@/lib/search-params";

export const productKeys = {
  search: (q: SearchQuery) => ["products", "search", q] as const,
  facets: (q: SearchQuery) => ["products", "facets", q] as const,
  detail: (slug: string) => ["products", "detail", slug] as const,
  similar: (slug: string) => ["products", "similar", slug] as const,
  store: (slug: string, q: SearchQuery) => ["products", "store", slug, q] as const,
};

export function useInfiniteProducts(query: SearchQuery, enabled = true) {
  return useInfiniteQuery({
    queryKey: productKeys.search(query),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      unwrap(api.GET("/api/v1/products/", { params: { query: { ...query, ...(pageParam ? { cursor: pageParam } : {}) } } })),
    getNextPageParam: (last) => cursorFrom(last.next),
    enabled,
    staleTime: 30_000,
  });
}

export function useFacets(query: SearchQuery) {
  const { cursor: _c, page_size: _p, sort: _s, ...rest } = query;
  return useQuery({
    queryKey: productKeys.facets(rest),
    queryFn: () => unwrap(api.GET("/api/v1/products/facets/", { params: { query: rest } })),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

export function useProduct(slug: string | undefined) {
  return useQuery({
    queryKey: productKeys.detail(slug ?? ""),
    queryFn: () =>
      unwrap(api.GET("/api/v1/products/{slug}/", { params: { path: { slug: slug! } }, headers: { "X-Session-ID": sessionId() } })),
    enabled: !!slug,
  });
}

export function useSimilar(slug: string | undefined) {
  return useQuery({
    queryKey: productKeys.similar(slug ?? ""),
    queryFn: () => unwrap(api.GET("/api/v1/products/{slug}/similar/", { params: { path: { slug: slug! } } })),
    enabled: !!slug,
    staleTime: 60_000,
  });
}

export function useInfiniteStoreProducts(slug: string | undefined, query: SearchQuery, enabled = true) {
  return useInfiniteQuery({
    queryKey: productKeys.store(slug ?? "", query),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      unwrap(
        api.GET("/api/v1/stores/{slug}/products/", {
          params: { path: { slug: slug! }, query: { ...query, ...(pageParam ? { cursor: pageParam } : {}) } },
        }),
      ),
    getNextPageParam: (last) => cursorFrom(last.next),
    enabled: !!slug && enabled,
  });
}

export function useReportProduct(slug: string) {
  return useMutation({
    mutationFn: (body: {
      reason: "COUNTERFEIT" | "PROHIBITED" | "SCAM" | "WRONG_CATEGORY" | "DUPLICATE" | "OFFENSIVE" | "SOLD" | "OTHER";
      details?: string;
    }) => unwrap(api.POST("/api/v1/products/{slug}/report/", { params: { path: { slug } }, body })),
  });
}
